import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import SeatMap from "@/components/SeatMap";
import { toast } from "sonner";

export default function Reservation() {
  const { showtimeId } = useParams();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [seats, setSeats] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [snacks, setSnacks] = useState({});
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [depositPct, setDepositPct] = useState(30);
  const [depositMethod, setDepositMethod] = useState("bar");
  const [familyDiscount, setFamilyDiscount] = useState({ percent: 10, min_tickets: 5, discount: 0 });
  const [walletInfo, setWalletInfo] = useState(null);

  useEffect(() => {
    api.get(`/showtimes/${showtimeId}`).then((r) => setSt(r.data));
    api.get(`/products`).then((r) => setProducts(r.data));
  }, [showtimeId]);

  const price = useMemo(() => (st?.ticket_price || 0) * seats.length, [st, seats]);
  const snackTotal = useMemo(() => Object.entries(snacks).reduce((s, [pid, q]) => {
    const p = products.find((x) => x.id === pid); return s + (p?.sell_price || 0) * q;
  }, 0), [snacks, products]);
  // Familientarif live abfragen (5+ Sitze)
  useEffect(() => {
    if (seats.length < 1 || price <= 0) { setFamilyDiscount((f) => ({ ...f, discount: 0 })); return; }
    api.get(`/pricing/family?ticket_count=${seats.length}&subtotal=${price}`)
      .then(({ data }) => setFamilyDiscount(data)).catch(() => {});
  }, [seats.length, price]);
  const familyOff = Number(familyDiscount?.discount || 0);
  const totalBrutto = Math.max(0, price + snackTotal - familyOff);
  const depositAmount = useMemo(() => {
    if (!depositEnabled || !seats.length) return 0;
    return +((totalBrutto * (depositPct / 100)).toFixed(2));
  }, [depositEnabled, depositPct, totalBrutto, seats.length]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Bitte Name eingeben");
    if (!seats.length) return toast.error("Bitte mindestens einen Platz wählen");
    setBusy(true);
    try {
      const { data } = await api.post("/reservations", {
        showtime_id: showtimeId,
        seats,
        guest_name: name,
        guest_email: email || null,
        guest_phone: phone || null,
        invite_code: inviteCode || null,
        snacks: Object.entries(snacks).filter(([_, q]) => q > 0).map(([product_id, quantity]) => ({ product_id, quantity })),
        deposit_amount: depositEnabled ? depositAmount : null,
        deposit_method: depositEnabled ? depositMethod : null,
      });
      toast.success(depositEnabled
        ? `Reservierung bestätigt · Anzahlung ${depositAmount.toFixed(2)} € via ${depositMethod === "ueberweisung" ? "Überweisung" : "Barzahlung an Kasse"}`
        : "Reservierung bestätigt");
      const token = data.reservation.cancel_token;
      navigate(`/reservierung/${token}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!st) return <div className="min-h-screen flex items-center justify-center text-[#A8A398]">Lade …</div>;

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-5xl mx-auto p-6 md:p-10">
        <Link to="/program" className="text-xs uppercase tracking-widest text-[#A8A398] hover:text-[#D4AF37]">← Zurück zum Programm</Link>
        <div className="mt-4 grid md:grid-cols-[220px_1fr] gap-6 items-start">
          {st.movie?.poster_url && <img src={st.movie.poster_url} alt="" className="w-48 aspect-[2/3] object-cover rounded" />}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Reservieren</div>
            <h1 className="font-heading text-4xl md:text-5xl">{st.movie?.title_de}</h1>
            <div className="text-[#A8A398] text-sm mt-1">{st.movie?.genre} · {st.movie?.duration_min} Min · {st.movie?.rating}</div>
            <div className="mt-3 flex gap-6 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-[#A8A398]">Datum</div><div className="font-mono">{st.date}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#A8A398]">Einlass</div><div className="font-mono">{st.entry_time}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#A8A398]">Start</div><div className="font-mono">{st.start_time}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#A8A398]">Saal</div><div className="font-mono">{st.hall}</div></div>
            </div>
            {st.movie?.description && <p className="mt-4 text-sm text-[#A8A398] leading-relaxed">{st.movie.description}</p>}
          </div>
        </div>

        <div className="card-lux p-6 mt-8">
          <h3 className="font-heading text-2xl mb-2">Sitzplatz wählen</h3>
          <SeatMap rows={st.seat_plan?.rows || []} seatTypes={st.seat_plan?.seat_types || []} seatStatus={st.seat_status || {}} selected={seats} onToggle={(id) => setSeats((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
            decorations={st.seat_plan?.decorations || []} backgroundUrl={st.seat_plan?.background_url || ""} backgroundOpacity={st.seat_plan?.background_opacity ?? 0.35}
            layers={st.seat_plan?.layers || []} freeMode={!!st.seat_plan?.free_mode} curve={st.seat_plan?.default_curve ?? 8} />
        </div>

        <div className="card-lux p-6 mt-6 grid md:grid-cols-2 gap-4">
          <F label="Name" v={name} on={setName} testid="reservation-name" />
          <F label="E-Mail (optional)" v={email} on={setEmail} type="email" />
          <F label="Telefon (optional)" v={phone} on={setPhone} />
          {st.is_private && <F label="Einladungscode" v={inviteCode} on={setInviteCode} />}
        </div>

        {st.snackbar_preorder && products.length > 0 && (
          <div className="card-lux p-6 mt-6">
            <h3 className="font-heading text-2xl mb-3">Snacks vorbestellen (optional)</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {products.filter((p) => p.available !== false).slice(0, 12).map((p) => (
                <div key={p.id} className="border border-[#2A2A2A] rounded p-3">
                  <div className="text-sm">{p.name}</div>
                  <div className="text-xs text-[#A8A398]">{p.sell_price?.toFixed(2)} €</div>
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => setSnacks((s) => ({ ...s, [p.id]: Math.max(0, (s[p.id] || 0) - 1) }))} className="w-7 h-7 border border-[#2A2A2A] rounded">−</button>
                    <span className="font-mono w-5 text-center">{snacks[p.id] || 0}</span>
                    <button onClick={() => setSnacks((s) => ({ ...s, [p.id]: (s[p.id] || 0) + 1 }))} className="w-7 h-7 border border-[#2A2A2A] rounded">+</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card-lux p-6 mt-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gesamt (Zahlung an der Kasse)</div>
            <div className="font-heading text-3xl text-[#D4AF37]" data-testid="reservation-total">{totalBrutto.toFixed(2)} €</div>
            {familyOff > 0 && (
              <div className="text-[11px] text-[#EEDC82] mt-1" data-testid="reservation-family-hint">
                Familientarif aktiv ({familyDiscount.percent}%): −{familyOff.toFixed(2)} € Rabatt ab {familyDiscount.min_tickets} Tickets
              </div>
            )}
            {depositEnabled && depositAmount > 0 && (
              <div className="text-[11px] text-[#EEDC82] mt-1" data-testid="reservation-deposit-hint">
                Reservierung vereinbart: Anzahlung {depositAmount.toFixed(2)} €{depositMethod === "ueberweisung" ? " per Überweisung" : " in bar an Kasse"} — noch zu bezahlen. Rest {Math.max(0, totalBrutto - depositAmount).toFixed(2)} € bei Einlass.
              </div>
            )}
          </div>
          <button data-testid="reservation-submit-btn" onClick={submit} disabled={busy} className="bg-[#D4AF37] text-black px-6 py-3 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] disabled:opacity-50">
            {busy ? "Buche …" : "Reservierung bestätigen"}
          </button>
        </div>

        {/* Anzahlung — optional */}
        {seats.length > 0 && (
          <div className="card-lux p-6 mt-6" data-testid="reservation-deposit-card">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={depositEnabled} onChange={(e) => setDepositEnabled(e.target.checked)}
                data-testid="reservation-deposit-toggle"
                className="mt-1 w-4 h-4 accent-[#D4AF37]" />
              <div className="flex-1">
                <div className="text-sm text-[#F5F0E6]">Anzahlung leisten (empfohlen ab 4+ Sitzplätzen)</div>
                <div className="text-[11px] text-[#A8A398] mt-1">
                  Sichern Sie Ihre Reservierung mit einer Anzahlung ab. Der Rest wird an der Kasse fällig.
                </div>
              </div>
            </label>
            {depositEnabled && (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Anzahlungshöhe · {depositPct}%</label>
                  <input type="range" min="10" max="100" step="10" value={depositPct} onChange={(e) => setDepositPct(Number(e.target.value))}
                    data-testid="reservation-deposit-slider"
                    className="w-full mt-2 accent-[#D4AF37]" />
                  <div className="flex justify-between text-[9px] text-[#575148] uppercase tracking-widest mt-1">
                    <span>10%</span><span>50%</span><span>100%</span>
                  </div>
                  <div className="font-heading text-2xl text-[#D4AF37] mt-2" data-testid="reservation-deposit-amount">
                    {depositAmount.toFixed(2)} €
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Zahlungsweg der Anzahlung</label>
                  <div className="space-y-1.5 mt-2">
                    {[
                      { v: "bar", l: "Bar an der Kasse (vor Vorstellung)" },
                      { v: "ueberweisung", l: "Überweisung (Kontodaten per E-Mail)" },
                    ].map((m) => (
                      <button key={m.v} type="button" onClick={() => setDepositMethod(m.v)}
                        data-testid={`reservation-deposit-method-${m.v}`}
                        className={`w-full text-left px-3 py-2 rounded border text-sm transitions-safe ${depositMethod === m.v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/40"}`}>
                        {m.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
function F({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input data-testid={testid} type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
    </div>
  );
}
